<template>
    <div class="dashboard">
        <PageBanner
            title="Client form dashboard"
            subtext="Select a form type to view its dashboard. Client-based forms also require a client selection."
        />

        <section class="dashboard-controls">
            <div class="dashboard-controls__inner">
                <div class="dashboard-controls__card">
                    <div class="dashboard-controls__label">Form type</div>
                    <q-select
                        v-model="selectedFormId"
                        dense
                        outlined
                        emit-value
                        map-options
                        option-label="title"
                        option-value="id"
                        :options="formOptions"
                        :loading="loadingForms"
                        :disable="loadingForms || !formOptions.length"
                        label="Select a form type"
                    >
                        <template v-slot:option="scope">
                            <q-item v-bind="scope.itemProps">
                                <q-item-section>
                                    <q-item-label>{{ scope.opt.title }}</q-item-label>
                                    <q-item-label caption>{{ scope.opt.description }}</q-item-label>
                                </q-item-section>
                            </q-item>
                        </template>
                    </q-select>
                </div>

                <div v-if="isAdminOrTeamLeader && !isKoTatouDashboard" class="dashboard-controls__card">
                    <div class="dashboard-controls__label">Programme or service</div>
                    <ProgrammeServiceSelector
                        v-model:selectedProgramme="selectedProgramme"
                        v-model:selectedService="selectedService"
                        :programme-options="programmeOptions"
                        :service-options="serviceOptions"
                    />
                </div>

                <div v-if="!isKoTatouDashboard" class="dashboard-controls__card">
                    <div class="dashboard-controls__label">Client select</div>
                    <q-select
                        v-model="selectedClient"
                        class="dashboard-controls__select"
                        dense
                        outlined
                        clearable
                        use-input
                        input-debounce="200"
                        option-label="full_name"
                        :options="displayClients"
                        :loading="loadingClients"
                        :disable="loadingClients || !isClientSelectionEnabled"
                        label="Search for a client"
                        placeholder="Name, NHI, email, phone, date of birth or ID"
                        @filter="filterFn"
                    >
                        <template v-slot:option="scope">
                            <q-item v-bind="scope.itemProps">
                                <q-item-section>
                                    <q-item-label>{{ scope.opt.full_name }}</q-item-label>
                                </q-item-section>
                                <q-item-section v-if="isTemporaryClientRow(scope.opt)" side>
                                    <q-badge color="teal" label="Internal" />
                                </q-item-section>
                            </q-item>
                        </template>
                    </q-select>
                </div>
            </div>
        </section>

        <DashboardBreakdownTable
            v-if="showBreakdown"
            :rows="breakdownRows"
            :loading="loadingClients"
            @select-client="selectClientFromBreakdown"
        />

        <div v-if="!formOptions.length && !loadingForms" class="dashboard-empty-state">
            No dashboard-enabled forms are currently available.
        </div>

        <KoTatouDashboard
            v-else-if="isKoTatouDashboard"
            :form-id="selectedFormId"
        />

        <div v-else-if="!selectedClient" class="dashboard-empty-state">
            Select a client to view the dashboard content for {{ selectedFormTitle }}.
        </div>

        <!-- Form-specific dashboard sections branch here so the selection shell stays shared. -->
        <TeItiKahurangiDashboard
            v-else-if="activeDashboardVariant === DASHBOARD_VARIANTS.TIK"
            :selected-client="selectedClient"
        />

        <BasicFormDashboard
            v-else
            :selected-client="selectedClient"
            :selected-form-id="selectedFormId"
            :selected-form-title="selectedFormTitle"
            @refresh-clients="refreshClientsPreserveSelection"
        />
    </div>
</template>

<script>
import api from "@/services/api/api";
import notify from "@/services/util/notify";
import token from "@/services/util/roleHandler";
import ProgrammeServiceSelector from "./ProgrammeServiceSelector.vue";
import PageBanner from "./DashboardComponents/PageBanner.vue";
import DashboardBreakdownTable from "./DashboardComponents/DashboardBreakdownTable.vue";
import TeItiKahurangiDashboard from "./DashboardComponents/TeItiKahurangiDashboard.vue";
import BasicFormDashboard from "./DashboardComponents/BasicFormDashboard.vue";
import KoTatouDashboard from "./DashboardComponents/KoTatouDashboard.vue";
import {
    DASHBOARD_VARIANTS,
    DEFAULT_DASHBOARD_FORM_ID,
    buildDashboardForms,
    getDashboardFormConfig
} from "./DashboardComponents/dashboardForms";
import { clientRowKey, isTemporaryClientRow } from "@/services/util/formSubject";
import { createPauaClientMatcher } from "@/services/util/pauaClientSearch";

export default {
    name: 'DashboardComponent',

    components: {
        BasicFormDashboard,
        DashboardBreakdownTable,
        KoTatouDashboard,
        PageBanner,
        ProgrammeServiceSelector,
        TeItiKahurangiDashboard
    },

    data() {
        return {
            formOptions: [],
            selectedFormId: DEFAULT_DASHBOARD_FORM_ID,
            selectedClient: null,
            selectedProgramme: null,
            selectedService: null,
            searchQuery: '',
            clients: [],
            loadingForms: false,
            loadingClients: false,
            loading: false,
            role: token.getRole(),
        };
    },

    async created() {
        await this.loadFormOptions();
    },

    computed: {
        DASHBOARD_VARIANTS() {
            return DASHBOARD_VARIANTS;
        },

        activeDashboardVariant() {
            return getDashboardFormConfig(this.selectedFormId).dashboardVariant;
        },

        isKoTatouDashboard() {
            return this.activeDashboardVariant === DASHBOARD_VARIANTS.KO_TATOU;
        },

        selectedForm() {
            return this.formOptions.find((form) => form.id === Number(this.selectedFormId)) || null;
        },

        selectedFormTitle() {
            return this.selectedForm?.title || 'the selected form';
        },

        isAdminOrTeamLeader() {
            return this.role === 'team_leader' || this.role === 'admin';
        },

        hasActiveServiceFilter() {
            return Boolean(this.selectedProgramme || this.selectedService);
        },

        isClientSelectionEnabled() {
            if (!this.selectedFormId) return false;

            if (!this.isAdminOrTeamLeader) {
                return true;
            }

            return this.hasActiveServiceFilter;
        },

        programmeOptions() {
            return [...new Set(this.clients.map((client) => client.programme_id).filter(Boolean))].sort();
        },

        serviceOptions() {
            return [...new Set(this.clients.map((client) => client.service_type_id).filter(Boolean))].sort();
        },

        filteredClients() {
            if (!this.isAdminOrTeamLeader) {
                return this.clients;
            }

            return this.clients.filter((client) => {
                // Temporary clients have no programme/service — keep them visible
                // regardless of the programme/service filter.
                if (isTemporaryClientRow(client)) {
                    return true;
                }

                if (this.selectedProgramme) {
                    return client.programme_id === this.selectedProgramme;
                }

                if (this.selectedService) {
                    return client.service_type_id === this.selectedService;
                }

                return false;
            });
        },

        displayClients() {
            if (!this.searchQuery) {
                return this.filteredClients;
            }

            return this.filteredClients.filter(createPauaClientMatcher(this.searchQuery));
        },

        breakdownRows() {
            return this.filteredClients
                .map((client) => ({
                    ...client,
                    row_key: clientRowKey(client),
                    days_since_last_completion: this.getDaysSince(client.latest_submitted_time)
                }))
                .sort((left, right) => left.full_name.localeCompare(right.full_name));
        },

        showBreakdown() {
            if (this.isKoTatouDashboard) return false;
            return !this.isAdminOrTeamLeader || this.hasActiveServiceFilter;
        }
    },

    watch: {
        async selectedFormId(newFormId, oldFormId) {
            if (newFormId === oldFormId) return;

            this.resetDashboardSelection();

            if (newFormId && !this.isKoTatouDashboard) {
                await this.loadClients();
            }
        },

        selectedProgramme() {
            this.resetSelectedClient();
        },

        selectedService() {
            this.resetSelectedClient();
        }
    },

    methods: {
        async loadFormOptions() {
            this.loadingForms = true;

            try {
                const response = await api.forms.getDynamicFormTypes();
                this.formOptions = buildDashboardForms(
                    response.data?.data || [],
                    token.getFeatures()
                );

                if (!this.formOptions.length) {
                    this.selectedFormId = null;
                    this.clients = [];
                    return;
                }

                const selectedFormExists = this.formOptions.some((form) => form.id === Number(this.selectedFormId));
                const nextFormId = selectedFormExists
                    ? Number(this.selectedFormId)
                    : (this.formOptions.find((form) => form.id === DEFAULT_DASHBOARD_FORM_ID)?.id || this.formOptions[0].id);

                const shouldLoadClientsDirectly = Number(this.selectedFormId) === nextFormId;
                this.selectedFormId = nextFormId;

                if (shouldLoadClientsDirectly) {
                    await this.loadClients();
                }
            } catch (error) {
                notify.error("Failed to load dashboard form types.", "top");
            } finally {
                this.loadingForms = false;
            }
        },

        async loadClients({ preserveSelectedClient = false } = {}) {
            if (!this.selectedFormId || this.isKoTatouDashboard) {
                this.clients = [];
                return;
            }

            this.loadingClients = true;
            const selectedKey = preserveSelectedClient ? clientRowKey(this.selectedClient) : null;

            try {
                const response = await api.client.getClientsFormCompleted(this.selectedFormId);
                this.clients = response.data || [];

                if (selectedKey) {
                    this.selectedClient = this.clients.find((client) => clientRowKey(client) === selectedKey) || this.selectedClient;
                }
            } catch (error) {
                notify.warning("Failed to retrieve client list");
            } finally {
                this.loadingClients = false;
            }
        },

        resetDashboardSelection() {
            this.clients = [];
            this.selectedProgramme = null;
            this.selectedService = null;
            this.resetSelectedClient();
        },

        resetSelectedClient() {
            this.searchQuery = '';
            this.selectedClient = null;
        },

        refreshClientsPreserveSelection() {
            return this.loadClients({ preserveSelectedClient: true });
        },

        getDaysSince(dateString) {
            if (!dateString) return 0;

            const timestamp = new Date(dateString).getTime();
            if (Number.isNaN(timestamp)) return 0;

            return Math.floor((Date.now() - timestamp) / 86400000);
        },

        selectClientFromBreakdown(row) {
            if (!row) return;
            const key = clientRowKey(row);
            this.selectedClient = this.clients.find((client) => clientRowKey(client) === key) || row;
        },

        filterFn(inputValue, update) {
            update(() => {
                this.searchQuery = inputValue;
            });
        },

        isTemporaryClientRow(row) {
            return isTemporaryClientRow(row);
        }
    }
};
</script>

<style scoped>
.dashboard {
    width: 100%;
    min-height: 100vh;
    overflow: hidden;
    background-color: white;
    box-shadow: 0 20px 40px rgba(47, 62, 70, 0.15);
}

.dashboard-controls {
    padding: 20px;
    background: #f8faf8;
    border-bottom: 1px solid rgba(47, 62, 70, 0.08);
}

.dashboard-controls__inner {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
    gap: 16px;
}

.dashboard-controls__card {
    padding: 16px;
    border-radius: 12px;
    background: white;
    box-shadow: 0 8px 25px rgba(47, 62, 70, 0.08);
}

.dashboard-controls__label {
    margin-bottom: 8px;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    color: #2f3e46;
}

.dashboard-controls__select :deep(.q-field__control) {
    background: white;
}

.dashboard-empty-state {
    margin: 20px;
    padding: 32px 20px;
    border: 2px dashed rgba(47, 62, 70, 0.12);
    border-radius: 20px;
    text-align: center;
    color: #607175;
    background: rgba(248, 250, 248, 0.9);
}

@media (max-width: 700px) {
    .dashboard-controls {
        padding: 16px 12px;
    }

    .dashboard-empty-state {
        margin: 16px 12px;
    }
}
</style>
